/**
 * A small force-directed layout — no dependency, because the SPA has none.
 *
 * The three forces are the classic ones (repulsion, springs, gravity), with two
 * deliberate choices that keep a portfolio-sized graph interactive:
 *
 *   - Repulsion is cut off at `REPULSE_RANGE` and evaluated through a spatial
 *     hash grid, so the cost is O(n · neighbours) instead of O(n²). A thousand
 *     nodes still step in well under a frame.
 *   - `alpha` cools towards zero and the loop stops once it is spent; any
 *     interaction (drag, filter, new data) reheats it. An idle graph therefore
 *     burns no CPU, which matters because this page tends to stay open.
 *
 * Positions live on the node objects themselves so the renderer can read them
 * without copying, and pinned nodes (`fx`/`fy` set while dragging) are simply
 * skipped by the integrator.
 */

export interface SimNode {
  readonly id: string;
  readonly klass: string;
  readonly label: string;
  readonly detail: string;
  /** Owner of the record (e.g. the Kunde of a Produktinstanz); '' when none. */
  readonly context: string;
  readonly state: string;
  readonly cardId: number;
  readonly degree: number;
  /** Radius in graph units — derived from the degree, cached for hit-testing. */
  readonly r: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Set while the node is dragged or pinned; the integrator leaves it alone. */
  fx: number | null;
  fy: number | null;
  /** Excluded from layout and rendering (class filter / search filter). */
  hidden: boolean;
}

export interface SimEdge {
  readonly id: string;
  readonly label: string;
  readonly source: SimNode;
  readonly target: SimNode;
}

const REPULSE_RANGE = 170;
const REPULSE_STRENGTH = 3400;
const SPRING_LENGTH = 78;
const SPRING_STIFFNESS = 0.035;
const GRAVITY = 0.012;
const DAMPING = 0.86;
const ALPHA_DECAY = 0.014;
const ALPHA_MIN = 0.004;
const MAX_SPEED = 30;

export function nodeRadius(degree: number): number {
  return Math.min(22, 6 + Math.sqrt(degree) * 2.1);
}

/** Deterministic pseudo-random in [0,1) — a stable seed means a stable layout. */
function hash01(seed: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * Seed positions on a spiral by class so the first frames already look sorted
 * instead of exploding out of a single point.
 */
export function seedPosition(node: SimNode, classIndex: number, classCount: number): void {
  const angle = (classIndex / Math.max(1, classCount)) * Math.PI * 2;
  const spread = 120 + hash01(node.id, 7) * 260;
  node.x = Math.cos(angle) * spread + (hash01(node.id, 13) - 0.5) * 120;
  node.y = Math.sin(angle) * spread + (hash01(node.id, 29) - 0.5) * 120;
}

export class ForceSimulation {
  nodes: SimNode[] = [];
  edges: SimEdge[] = [];
  alpha = 1;

  /** class name -> attractor angle, used only when `groupByClass` is on. */
  private classAngles = new Map<string, number>();
  groupByClass = false;

  setGraph(nodes: SimNode[], edges: SimEdge[], classOrder: readonly string[]): void {
    this.nodes = nodes;
    this.edges = edges;
    this.classAngles.clear();
    classOrder.forEach((klass, index) => {
      this.classAngles.set(klass, (index / Math.max(1, classOrder.length)) * Math.PI * 2);
    });
    this.reheat(1);
  }

  reheat(to = 0.6): void {
    this.alpha = Math.max(this.alpha, to);
  }

  get running(): boolean {
    return this.alpha > ALPHA_MIN;
  }

  step(): void {
    const active = this.nodes.filter((n) => !n.hidden);
    if (active.length === 0) {
      this.alpha = 0;
      return;
    }
    const alpha = this.alpha;

    // --- repulsion (spatial hash, cut off at REPULSE_RANGE) -----------------
    const cell = REPULSE_RANGE;
    const buckets = new Map<string, SimNode[]>();
    for (const node of active) {
      const key = `${Math.floor(node.x / cell)},${Math.floor(node.y / cell)}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(node);
      else buckets.set(key, [node]);
    }
    for (const node of active) {
      const cx = Math.floor(node.x / cell);
      const cy = Math.floor(node.y / cell);
      for (let ix = cx - 1; ix <= cx + 1; ix += 1) {
        for (let iy = cy - 1; iy <= cy + 1; iy += 1) {
          const bucket = buckets.get(`${ix},${iy}`);
          if (!bucket) continue;
          for (const other of bucket) {
            if (other === node) continue;
            let dx = node.x - other.x;
            let dy = node.y - other.y;
            let dist2 = dx * dx + dy * dy;
            if (dist2 > REPULSE_RANGE * REPULSE_RANGE) continue;
            if (dist2 < 0.01) {
              // Perfectly coincident nodes would divide by zero — nudge them
              // apart deterministically instead of randomly, so a re-layout of
              // the same data lands in the same place.
              dx = (hash01(node.id, 3) - 0.5) * 0.5;
              dy = (hash01(node.id, 5) - 0.5) * 0.5;
              dist2 = dx * dx + dy * dy + 0.01;
            }
            const dist = Math.sqrt(dist2);
            const force = (REPULSE_STRENGTH * alpha) / dist2;
            node.vx += (dx / dist) * force;
            node.vy += (dy / dist) * force;
          }
        }
      }
    }

    // --- springs ------------------------------------------------------------
    for (const edge of this.edges) {
      const a = edge.source;
      const b = edge.target;
      if (a.hidden || b.hidden) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 0.01;
      const force = (dist - SPRING_LENGTH) * SPRING_STIFFNESS * alpha;
      const ux = (dx / dist) * force;
      const uy = (dy / dist) * force;
      a.vx += ux;
      a.vy += uy;
      b.vx -= ux;
      b.vy -= uy;
    }

    // --- gravity / class clustering ----------------------------------------
    for (const node of active) {
      let tx = 0;
      let ty = 0;
      if (this.groupByClass) {
        const angle = this.classAngles.get(node.klass) ?? 0;
        const ring = 320;
        tx = Math.cos(angle) * ring;
        ty = Math.sin(angle) * ring;
      }
      node.vx += (tx - node.x) * GRAVITY * alpha;
      node.vy += (ty - node.y) * GRAVITY * alpha;
    }

    // --- integrate ----------------------------------------------------------
    for (const node of active) {
      if (node.fx !== null && node.fy !== null) {
        node.x = node.fx;
        node.y = node.fy;
        node.vx = 0;
        node.vy = 0;
        continue;
      }
      node.vx *= DAMPING;
      node.vy *= DAMPING;
      const speed = Math.hypot(node.vx, node.vy);
      if (speed > MAX_SPEED) {
        node.vx = (node.vx / speed) * MAX_SPEED;
        node.vy = (node.vy / speed) * MAX_SPEED;
      }
      node.x += node.vx;
      node.y += node.vy;
    }

    this.alpha -= this.alpha * ALPHA_DECAY;
    if (this.alpha <= ALPHA_MIN) this.alpha = 0;
  }

  /** Topmost visible node under a point in graph coordinates (null if none). */
  find(x: number, y: number, tolerance = 4): SimNode | null {
    let best: SimNode | null = null;
    let bestDist = Infinity;
    for (const node of this.nodes) {
      if (node.hidden) continue;
      const dist = Math.hypot(node.x - x, node.y - y);
      if (dist <= node.r + tolerance && dist < bestDist) {
        best = node;
        bestDist = dist;
      }
    }
    return best;
  }

  /** Bounding box of the visible nodes, or null when nothing is visible. */
  bounds(): { minX: number; minY: number; maxX: number; maxY: number } | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let any = false;
    for (const node of this.nodes) {
      if (node.hidden) continue;
      any = true;
      minX = Math.min(minX, node.x - node.r);
      minY = Math.min(minY, node.y - node.r);
      maxX = Math.max(maxX, node.x + node.r);
      maxY = Math.max(maxY, node.y + node.r);
    }
    return any ? { minX, minY, maxX, maxY } : null;
  }
}
